import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Layers, ArrowRight, X } from "lucide-react";

const dismissKey = (p) => `nudge_dismissed_${p}`;
const slug = (p) => p.replace(/[\s&]+/g, "-");

// A quiet prompt when a life stage has gathered enough memories to become a chapter.
export function ChapterNudges() {
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [working, setWorking] = useState(null);

  useEffect(() => {
    api.get("/chapter-nudges").then(({ data }) => {
      const visible = (data || []).filter((n) => !localStorage.getItem(dismissKey(n.period)));
      setNudges(visible);
    }).catch(() => {});
  }, []);

  const bringTogether = async (n) => {
    setWorking(n.period);
    try {
      const { data } = await api.post("/chapters/generate", { title: n.period, memory_ids: n.memory_ids });
      toast.success("Your chapter is ready — everything in it came from your own memories");
      navigate(`/story/${data.id}`);
    } catch {
      toast.error("We couldn't create that chapter just now. Please try again.");
      setWorking(null);
    }
  };
  const dismiss = (p) => { localStorage.setItem(dismissKey(p), "1"); setNudges((ns) => ns.filter((n) => n.period !== p)); };

  if (!nudges.length) return null;
  return (
    <div data-testid="chapter-nudges" className="space-y-3">
      {nudges.map((n) => (
        <div key={n.period} data-testid={`chapter-nudge-${slug(n.period)}`}
          className="rounded-2xl bg-white border border-line p-5 flex items-start gap-4 animate-fade-up">
          <span className="w-11 h-11 rounded-2xl bg-sagelight text-teal flex items-center justify-center shrink-0"><Layers size={20} /></span>
          <div className="flex-1">
            <p className="text-charcoal leading-relaxed">
              You've now shared <b className="text-teal">{n.count}</b> memories that belong together in <b>{n.period}</b>. Would you like me to bring them into a chapter?
            </p>
            <div className="mt-3 flex items-center gap-3">
              <button data-testid={`nudge-create-${slug(n.period)}`} onClick={() => bringTogether(n)} disabled={working === n.period}
                className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors disabled:opacity-60">
                {working === n.period ? <><Loader2 className="animate-spin" size={15} /> Bringing them together…</> : <>Bring them together <ArrowRight size={15} /></>}
              </button>
              <button data-testid={`nudge-dismiss-${slug(n.period)}`} onClick={() => dismiss(n.period)} className="text-subtle hover:text-charcoal text-sm">Not now</button>
            </div>
          </div>
          <button aria-label="Dismiss" onClick={() => dismiss(n.period)} className="text-subtle hover:text-charcoal"><X size={16} /></button>
        </div>
      ))}
    </div>
  );
}
